/**
 * Expenses report — the workbook's column H, company-wide.
 *
 * Read path for the week. Writes go through the same /api/reports/expenses
 * endpoint that Contractor 360 uses, so there is one write path.
 */

import { api, type ExpensesReportData } from '../api';
import { Card, CardHeader, Cell, ExportButton, Row, Spinner, Table } from '../components/Ui';
import { UnifiedTimeFilter } from '../components/UnifiedTimeFilter';
import { useTimeFilter } from '../context/TimeFilterContext';
import { KpiCard } from '../components/KpiCard';
import { useAsyncData } from '../hooks/useAsyncData';
import { money, moneyCompact, shortDate } from '../format';
import { Shell, type ShellProps } from '../Shell';
import { ReportShell } from './ReportShell';

export function ExpensesReport(props: ShellProps) {
  const tf = useTimeFilter();
  const canWrite = props.session.permissions.includes('time:write');

  const { data, error, loading, reload } = useAsyncData<ExpensesReportData>(
    () => api.expensesReport(tf.toWeekEnding()),
    [tf.selection],
    'Could not load expenses.',
  );

  const onDelete = async (id: string) => {
    await api.deleteExpense(id);
    reload();
  };

  return (
    <Shell {...props}>
      <ReportShell
        title="Expenses"
        subtitle="Per-contractor deductions for the selected week"
        weekEnding={data?.weekEnding}
        actions={
          <>
            {data && data.expenses.length > 0 && (
              <ExportButton
                data={data.expenses.map((e) => ({
                  contractor: e.workerName,
                  client: e.clientName,
                  amount: e.amount,
                  description: e.description ?? '',
                  enteredBy: e.createdBy ?? '',
                }))}
                filename={`expenses-${data.weekEnding ?? 'latest'}`}
                headers={[
                  { key: 'contractor', label: 'Contractor' },
                  { key: 'client', label: 'Client' },
                  { key: 'amount', label: 'Amount' },
                  { key: 'description', label: 'Description' },
                  { key: 'enteredBy', label: 'Entered by' },
                ]}
              />
            )}
            <UnifiedTimeFilter />
          </>
        }
      >
        {error && <p className="text-sm text-red-600">{error}</p>}
        {loading && (
          <div className="flex justify-center py-12">
            <Spinner label="Loading expenses…" />
          </div>
        )}

        {data && data.weekEnding && (
          <>
            <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3">
              <KpiCard
                label="Total expenses"
                value={moneyCompact(data.total)}
                exact={money(data.total)}
                tone="caution"
                delay={0}
              />
              <KpiCard
                label="Entries"
                value={String(data.expenses.length)}
                subtitle="One per contractor per week"
                delay={40}
              />
              <KpiCard
                label="Week"
                value={shortDate(data.weekEnding)}
                subtitle="Enter new amounts from Contractor 360"
                delay={80}
              />
            </div>

            <Card>
              <CardHeader
                title="Expense entries"
                subtitle="Deducted from GP when the period closes"
              />
              {data.expenses.length === 0 ? (
                <p className="px-4 pb-4 text-sm text-ink-muted">
                  No expenses recorded for this week. Open a contractor and edit the Expenses column
                  while the period is still open.
                </p>
              ) : (
                <Table
                  head={[
                    { label: 'Contractor' },
                    { label: 'Client' },
                    { label: 'Amount', align: 'right' },
                    { label: 'Description' },
                    { label: 'Entered by' },
                    ...(canWrite ? [{ label: 'Actions' }] : []),
                  ]}
                >
                  {data.expenses.map((e) => (
                    <Row key={e.id}>
                      <Cell>{e.workerName}</Cell>
                      <Cell muted>{e.clientName}</Cell>
                      <Cell align="right" mono>
                        {money(e.amount)}
                      </Cell>
                      <Cell muted>{e.description ?? ''}</Cell>
                      <Cell muted>{e.createdBy ?? ''}</Cell>
                      {canWrite && (
                        <Cell>
                          <button
                            type="button"
                            onClick={() => {
                              void onDelete(e.id);
                            }}
                            className="text-xs text-ink-faint hover:text-red-600"
                          >
                            Delete
                          </button>
                        </Cell>
                      )}
                    </Row>
                  ))}
                  <Row>
                    <Cell>
                      <span className="font-semibold text-ink">Total</span>
                    </Cell>
                    <Cell>{''}</Cell>
                    <Cell align="right" mono>
                      <span className="font-semibold">{money(data.total)}</span>
                    </Cell>
                    <Cell>{''}</Cell>
                    <Cell>{''}</Cell>
                    {canWrite && <Cell>{''}</Cell>}
                  </Row>
                </Table>
              )}
            </Card>
          </>
        )}

        {data && !data.weekEnding && !loading && (
          <Card>
            <p className="px-4 py-8 text-center text-sm text-ink-muted">
              No hours have been imported yet, so there is no expense week to show.
            </p>
          </Card>
        )}
      </ReportShell>
    </Shell>
  );
}
